import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { useEffect } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/lib/cn'
import { type Toast, type ToastVariant, useToastStore } from '@/stores/toast'

const ICONS: Record<ToastVariant, typeof Info> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
}

const TONES: Record<ToastVariant, string> = {
  success: 'text-success',
  error: 'text-danger',
  warning: 'text-warning',
  info: 'text-info',
}

function ToastRow({ toast }: { toast: Toast }) {
  const dismiss = useToastStore((state) => state.dismiss)
  const Icon = ICONS[toast.variant]

  useEffect(() => {
    if (toast.duration <= 0) return
    const timer = setTimeout(() => dismiss(toast.id), toast.duration)
    return () => clearTimeout(timer)
  }, [toast.id, toast.duration, dismiss])

  return (
    <div
      role="status"
      className={cn(
        'bg-overlay border-border elevated-lg animate-slide-in-right pointer-events-auto',
        'flex w-full max-w-sm items-start gap-3 rounded-lg border p-3',
      )}
    >
      <Icon
        className={cn('mt-0.5 size-4 shrink-0', TONES[toast.variant])}
        strokeWidth={2}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] leading-snug font-medium">{toast.title}</p>
        {toast.description && (
          <p className="text-muted-foreground mt-0.5 text-[12px] leading-snug break-words">
            {toast.description}
          </p>
        )}
        {toast.action && (
          <button
            onClick={() => {
              toast.action?.onClick()
              dismiss(toast.id)
            }}
            className="text-primary-text mt-2 text-[12px] font-medium hover:underline"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <button
        onClick={() => dismiss(toast.id)}
        className="text-subtle-foreground hover:text-foreground -m-1 shrink-0 p-1 transition-colors"
        aria-label="Dismiss"
      >
        <X className="size-3.5" strokeWidth={2} />
      </button>
    </div>
  )
}

export function Toaster() {
  const toasts = useToastStore((state) => state.toasts)
  if (toasts.length === 0) return null

  return createPortal(
    <div className="pointer-events-none fixed right-4 bottom-4 z-[60] flex flex-col items-end gap-2">
      {toasts.map((toast) => (
        <ToastRow key={toast.id} toast={toast} />
      ))}
    </div>,
    document.body,
  )
}
